import { FeatureFlag, ResolverTag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import ChestCount from '@swf/components/StarsBadge/ChestCount';
import CounterBadge from '@swf/components/StarsBadge/CounterBadge';
import TournamentMoneyAmount from '@swf/components/StarsBadge/TournamentMoneyAmount';
import { select } from '@swf/core/store';

import Bonuses from '../../assets/icons/pr-bonuses.svg';
import Chests from '../../assets/icons/pr-chests.svg';
import FreeBets from '../../assets/icons/pr-free-bets.svg';
import FreeSpins from '../../assets/icons/pr-free-spins.svg';
import InstantBonuses from '../../assets/icons/pr-instant-bonuses.svg';
import PrizeGames from '../../assets/icons/pr-prize-games.svg';
import BonusCash from '../../assets/icons/pr-sports-bonus-cash.svg';
import TournamentMoney from '../../assets/icons/pr-tournament-money.svg';
import TournamentTickets from '../../assets/icons/pr-tournament-tickets.svg';

import {
  TXTCLI_Bonuses,
  TXTCLI_Chests,
  TXTCLI_Free_Bets,
  TXTCLI_Free_Spins,
  TXTCLI_Instant_Bonuses,
  TXTCLI_Prize_Games,
  TXTCLI_Tournament_Money,
  TXTCLI_Tournament_Tickets,
  TXTWCL_Sports_Bonus_Cash
} from './useMyRewardsNavItems.messages.json';

const useMyRewardsNavItems = () => {
  const chestFlag = useFeature(FeatureFlag.Chest);
  const freeBetsFlag = useFeature(FeatureFlag.FreeBets);
  const ispFreeBetsFlag = useFeature(FeatureFlag.ISPMyStarsFreeBets);
  const freeSpinsFlag = useFeature(FeatureFlag.FreeSpins);
  const instantBonusesFlag = useFeature(FeatureFlag.InstantBonusesVisibility);
  const prizeGamesFlag = useFeature(FeatureFlag.MiniGameTickets);
  const releaseBonusesFlag = useFeature(FeatureFlag.ReleaseBonuses);
  const sportsBonusCashFlag = useFeature(FeatureFlag.SportsBonusCash);
  const tournamentMoneyFlag = useFeature(FeatureFlag.TournamentMoney);
  const tournamentTicketsFlag = useFeature(FeatureFlag.TournamentTickets);
  const embedded = useSelector(select.device.isEmbedded);

  const myRewardsNavItems = useMemo(
    () =>
      [
        {
          title: TXTCLI_Chests,
          href: '/chests',
          icon: Chests,
          badge: ChestCount,
          visible: chestFlag
        },
        {
          title: TXTCLI_Bonuses,
          href: '/bonus/bonuses',
          icon: Bonuses,
          badge: () => <CounterBadge counter="releaseBonusCounter" />,
          visible: releaseBonusesFlag
        },
        {
          title: TXTCLI_Prize_Games,
          href: '/bonus/game-tickets',
          icon: PrizeGames,
          badge: () => <CounterBadge counter="prizeGameCounter" />,
          visible: prizeGamesFlag
        },
        {
          title: TXTCLI_Tournament_Tickets,
          href: '/tournament-tickets',
          icon: TournamentTickets,
          badge: () => <CounterBadge counter="tournamentTicketCounter" />,
          visible: tournamentTicketsFlag
        },
        {
          title: TXTCLI_Tournament_Money,
          href: '/tournament-money',
          icon: TournamentMoney,
          badge: TournamentMoneyAmount,
          visible: tournamentMoneyFlag
        },
        {
          title: TXTCLI_Free_Spins,
          href: '/bonus/free-spin-bonuses',
          icon: FreeSpins,
          badge: () => <CounterBadge counter="freeSpinCounter" />,
          visible: freeSpinsFlag
        },
        {
          title: TXTCLI_Instant_Bonuses,
          href: '/bonus/instant-bonuses',
          icon: InstantBonuses,
          badge: () => <CounterBadge counter="instantBonusCounter" />,
          visible: instantBonusesFlag
        },
        {
          title: TXTCLI_Free_Bets,
          icon: FreeBets,
          visible: freeBetsFlag,
          ...(ispFreeBetsFlag
            ? {
                href: '/bonus/free-bets',
                badge: () => <CounterBadge counter="ispFreeBetsCounter" />
              }
            : {
                resolverTag: !embedded ? ResolverTag.SportsBookFreeBets : undefined,
                badge: () => <CounterBadge counter="freeBetCounter" />,
                onClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                  if (embedded) {
                    event.preventDefault();
                    window.starsweb?.app.execute('sports');
                  }
                }
              })
        },
        {
          title: TXTWCL_Sports_Bonus_Cash,
          href: '/bonus/sports-bonus-cash',
          icon: BonusCash,
          badge: () => <CounterBadge counter="sportsCashBonusCounter" />,
          visible: sportsBonusCashFlag
        }
      ].filter(({ visible }) => !!visible),
    [
      chestFlag,
      releaseBonusesFlag,
      freeBetsFlag,
      freeSpinsFlag,
      instantBonusesFlag,
      prizeGamesFlag,
      sportsBonusCashFlag,
      ispFreeBetsFlag,
      embedded,
      tournamentMoneyFlag,
      tournamentTicketsFlag
    ]
  );
  return myRewardsNavItems;
};

export default useMyRewardsNavItems;
